import { useEffect, useRef, useState } from 'react';
import { cldImg } from '../../utils/cloudinary';

function playCelebrationSound() {
  try {
    const audio = new Audio('/pop.mp3');
    audio.volume = 0.8;
    audio.play().catch(() => {});
  } catch {}
}

const COLORS = [
  '#F5B400', '#FF6B6B', '#4ECDC4', '#A8E6CF',
  '#FFD93D', '#FD79A8', '#6C5CE7', '#00B894',
  '#FF8B94', '#96CEB4', '#E17055', '#45B7D1',
];

function ConfettiCanvas() {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const pieces = Array.from({ length: 100 }, () => {
      const isString = Math.random() < 0.3;
      return {
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 150,
        w: isString ? 2 + Math.random() * 2 : 6 + Math.random() * 8,
        h: isString ? 15 + Math.random() * 20 : 5 + Math.random() * 7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 8,
        vx: (Math.random() - 0.5) * 3,
        vy: 2 + Math.random() * 3,
        isString,
        wave: Math.random() * Math.PI * 2,
        waveSpeed: 0.05 + Math.random() * 0.05,
        waveAmp: 1 + Math.random() * 2,
        opacity: 1,
      };
    });

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.forEach(p => {
        p.y += p.vy;
        p.x += p.vx + Math.sin(p.wave) * p.waveAmp;
        p.wave += p.waveSpeed;
        p.rotation += p.rotationSpeed;
        if (p.y > canvas.height * 0.7) p.opacity = Math.max(0, p.opacity - 0.02);
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        if (p.isString) {
          ctx.beginPath();
          ctx.moveTo(0, -p.h / 2);
          for (let i = 0; i <= p.h; i += 2) ctx.lineTo(Math.sin(i * 0.4) * p.w * 2, -p.h / 2 + i);
          ctx.lineWidth = p.w;
          ctx.strokeStyle = p.color;
          ctx.stroke();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      });
      rafRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-[998]" style={{ width: '100vw', height: '100vh' }} />;
}

export default function FreeGiftCelebration({ gift, onClose }) {
  useEffect(() => {
    playCelebrationSound();
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <>
      <ConfettiCanvas />
      <div className="fixed inset-0 z-[999] flex items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50" />
        <div
          className="relative z-10 bg-white rounded-3xl px-8 py-7 text-center shadow-2xl max-w-xs mx-4"
          style={{ animation: 'gift-pop-in 0.4s cubic-bezier(0.175,0.885,0.32,1.275) forwards' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Gift image or emoji */}
          <div className="flex items-center justify-center mb-3">
            {gift?.img ? (
              <div className="relative">
                <div
                  className="w-24 h-24 rounded-full overflow-hidden border-4 border-orange-400 shadow-lg"
                  style={{ animation: 'gift-bounce 0.5s ease infinite alternate' }}
                >
                  <img src={cldImg(gift.img, 200)} alt={gift.name} className="w-full h-full object-cover" />
                </div>
                <span className="absolute -top-2 -right-2 text-2xl" style={{ animation: 'gift-bounce 0.4s ease infinite alternate' }}>🎁</span>
              </div>
            ) : (
              <div className="text-6xl" style={{ animation: 'gift-bounce 0.5s ease infinite alternate' }}>🎁</div>
            )}
          </div>

          <h2 className="text-xl font-black text-gray-800 mb-1">Free Gift Unlocked!</h2>
          <p className="text-sm font-semibold text-orange-500 mb-1 line-clamp-2">{gift?.name}</p>
          <p className="text-xs text-gray-400 mb-5">Added to your cart automatically!</p>

          <button
            onClick={onClose}
            className="bg-orange-400 hover:bg-orange-500 text-white font-bold px-8 py-2.5 rounded-full text-sm transition-colors"
          >
            Awesome! 🎊
          </button>
        </div>
      </div>

      <style>{`
        @keyframes gift-pop-in {
          0%   { transform: scale(0.4); opacity: 0; }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes gift-bounce {
          from { transform: scale(1)   rotate(-6deg); }
          to   { transform: scale(1.2) rotate(6deg);  }
        }
      `}</style>
    </>
  );
}

// Hook — triggers celebration when a new gift tier is unlocked
export function useGiftUnlock(cartTotal, freeTiers) {
  const prevTotal = useRef(cartTotal);
  const unlockedTiers = useRef(new Set());
  const [celebrateGift, setCelebrateGift] = useState(null); // { name, img }

  useEffect(() => {
    if (!freeTiers || freeTiers.length === 0) return;

    freeTiers.forEach(t => {
      const amount = parseFloat(t.min_amount);

      // Reset if cart drops below threshold
      if (cartTotal < amount) {
        unlockedTiers.current.delete(t.id);
        return;
      }

      // New unlock — crossed threshold for first time since last drop
      if (!unlockedTiers.current.has(t.id) && prevTotal.current < amount && cartTotal >= amount) {
        unlockedTiers.current.add(t.id);
        setCelebrateGift({
          name: t.product?.name || 'Free Gift',
          img: t.product?.images?.[0]?.image_url || null,
        });
        const timer = setTimeout(() => setCelebrateGift(null), 5000);
        prevTotal.current = cartTotal;
        return () => clearTimeout(timer);
      }
    });

    prevTotal.current = cartTotal;
  }, [cartTotal, freeTiers]);

  return [celebrateGift, () => setCelebrateGift(null)];
}
